import type * as schemas from "@common/schemas/v2";
import * as models from "@common/models/v2";
import { BaseStep } from "./base";
import { StepActions } from "./step";
import { FlowFlexibility } from "@common/schemas";
import { VideoBlock } from "@common/schemas/form/html-content";
import { makeObservable, observable, override } from "mobx";

export class ShowContentStep extends BaseStep {
  attributes: models.ShowContentStepAttributes;
  actions: StepActions;

  // Track which videos have been played to completion
  playedVideoIds: Set<string> = new Set();

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    super(step, actions, flowFlexibility);
    this.attributes = new models.ShowContentStepAttributes(step.attributes as schemas.ShowContentStepAttributes);
    this.actions = actions;

    makeObservable(this, {
      playedVideoIds: observable,
      isBlocking: override,
    });
  }

  get additionalContent() {
    return this.attributes.content;
  }

  get videoBlocks(): VideoBlock[] {
    if (!this.attributes.contentState) return [];
    const videos = this.attributes.contentState.filter((block): block is VideoBlock => block.type === "video");
    return videos;
  }

  get hasVideoContent(): boolean {
    const hasVideo = this.videoBlocks.length > 0;
    return hasVideo;
  }

  get isBlockingStepType(): boolean {
    return this.hasVideoContent && this.attributes.shouldPlayAtLeastOnce;
  }

  get isBlocking(): boolean {
    if (!this.hasVideoContent || !this.attributes.shouldPlayAtLeastOnce) {
      return false;
    }

    // Block until all videos have been played at least once
    const requiredVideoIds = this.videoBlocks.map((block) => block.id);
    const isBlocking = !requiredVideoIds.every((id) => this.playedVideoIds.has(id));
    return isBlocking;
  }

  onVideoPlayedToCompletion(videoId: string) {
    this.playedVideoIds.add(videoId);
  }

  async run() {
    super.run();
    // Reset tracking when step runs
    this.playedVideoIds.clear();
  }
}
